import { useState } from 'react';
import { api, useClientRole, useSign, useWorkspace } from '@/api';
import { studioApi } from '@/api/studio';
import type { SignRequest } from '@/api/types';
import { formatWhen } from '@/lib/format';
import { DownloadButton } from './DownloadButton';
import { Dialog } from './ui';

/**
 * Simple e-signature (63-ФЗ, by the clause in the contract): the signer confirms
 * they read the document and types their name; time and file fingerprint are kept.
 */
export function SignDialog({ request, onClose }: { request: SignRequest; onClose: () => void }) {
  return request.status === 'signed' ? <SignatureView request={request} onClose={onClose} /> : <SignForm request={request} onClose={onClose} />;
}

/**
 * A document that was sent for signature, read-only: the file to open or download and, once
 * signed, who signed it, when, and the fingerprint of the file. Both sides open it after signing.
 */
export function SignatureView({ request, onClose, studio }: { request: SignRequest; onClose: () => void; studio?: boolean }) {
  const signed = request.status === 'signed';
  const load = () => (studio ? studioApi : api).downloadDocument(request.documentId!);
  return (
    <Dialog open onClose={onClose} title={`${signed ? 'Подписанный документ' : 'Документ'}: ${request.title}`}>
      <div className="space-y-4 p-5">
        <div className="rounded bg-panel p-3 text-sm shadow-hair">
          <div className="font-medium">{request.documentName}</div>
          {request.documentId ? (
            <span className="mt-1 flex items-center gap-2">
              <DownloadButton className="text-muted underline underline-offset-2" name={request.documentName} label="Открыть документ" load={load}>
                Открыть документ
              </DownloadButton>
              <DownloadButton name={request.documentName} load={load} />
            </span>
          ) : (
            <p className="mt-1 text-overdue">Файла нет</p>
          )}
        </div>
        {signed ? (
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm" aria-label="Подпись">
            <dt className="text-muted">Подписал</dt>
            <dd>{request.signedBy}</dd>
            <dt className="text-muted">Когда</dt>
            <dd>{request.signedAt ? formatWhen(request.signedAt) : '—'}</dd>
            {request.hash && (
              <>
                <dt className="text-muted">Отпечаток файла</dt>
                <dd className="break-all font-mono text-xs">{request.hash}</dd>
              </>
            )}
          </dl>
        ) : (
          <p className="text-sm text-muted">Отправлен на подпись {formatWhen(request.createdAt)}. Подписывает владелец компании.</p>
        )}
      </div>
    </Dialog>
  );
}

function SignForm({ request, onClose }: { request: SignRequest; onClose: () => void }) {
  const { data: ws } = useWorkspace();
  const sign = useSign();
  const owner = useClientRole() === 'owner';
  const [agree, setAgree] = useState(false);
  const [name, setName] = useState('');
  return (
    <Dialog open onClose={onClose} title={`Подписать: ${request.title}`}>
      <div className="space-y-4 p-5">
        <div className="rounded bg-panel p-3 text-sm shadow-hair">
          <div className="font-medium">{request.documentName}</div>
          {request.documentId ? (
            <DownloadButton
              className="mt-1 text-muted underline underline-offset-2"
              name={request.documentName}
              label="Открыть документ"
              load={() => api.downloadDocument(request.documentId!)}
            >
              Открыть документ
            </DownloadButton>
          ) : (
            <p className="mt-1 text-overdue">Документа нет — попросите студию прислать файл</p>
          )}
        </div>
        {!owner && <p className="text-sm text-muted">Подписывает владелец компании</p>}
        {owner && request.documentId && (
          <>
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" className="mt-0.5" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
              <span>Я прочитал документ и подписываю его простой электронной подписью по договору со студией.</span>
            </label>
            <label className="block text-sm">
              <span className="mb-1.5 block text-muted">Имя и фамилия, как в документе</span>
              <input className="field" value={name} onChange={(e) => setName(e.target.value)} placeholder={ws?.me.name} />
            </label>
            {sign.error && <p className="text-sm text-overdue">{(sign.error as Error).message}</p>}
            <button className="btn-primary w-full" disabled={!agree || !name.trim() || sign.isPending} onClick={() => sign.mutate({ id: request.id, name }, { onSuccess: onClose })}>
              Подписать
            </button>
            <p className="text-xs text-muted">Сохраним время, ваше имя и отпечаток файла. Подписанный документ появится в «Документах».</p>
          </>
        )}
      </div>
    </Dialog>
  );
}
